/**
 * ENG-5757: what each engagement term means, defined once.
 *
 * The Settings tab listed ten governance and billing terms as bare labels over
 * bare numbers. These decide when an approval is forced, how long hours can
 * wait and what OnFrontiers does about a dispute, so a customer who cannot
 * read them cannot consent to them (Notion FB-198, Brian, 2026-09-10).
 *
 * Every surface that names one of these terms imports its label and
 * definition from here: the Settings tab, the hire drawer and the expert's
 * offer terms. A second copy of a definition is exactly the drift this file
 * exists to prevent, so edit the sentence here and nowhere else.
 *
 * **These are policy statements, not UI copy.** They have to agree with the
 * published Engagement Terms (Rev 2.1, onfrontiers.com/policies/engagement-terms)
 * and with Brian's S13 Messaging Doc, the single source for customer-facing
 * engagements copy. Changes go through Brian, not through a code review
 * alone.
 *
 * Each definition is one sentence that says what happens when the setting
 * fires, and each is written against what the api actually does, not what an
 * older spec planned. Three of the original labels did not survive that:
 *
 * - **Escalation window** read `escalate_days`, a column nothing in the api
 *   reads (`engagement/service/read_models_staff.go` says so). The step that
 *   really runs first is the reminder on `reminder_days`, so that is what the
 *   row now shows, as "Review reminder".
 * - **Dispute arbitration** contradicted the published terms: Engagement Terms
 *   §2.8 says "OnFrontiers does not determine the dispute" and gives a
 *   non-binding recommendation after 14 days. ET Conformance Spec 1 names the
 *   setting "Dispute recommendation"; this uses that name and its wording.
 * - **Mediation window** keeps its name. It is the second notice to the work
 *   approvers, and that email offers OnFrontiers' help
 *   (`emails/EngagementHoursMediation.html` in the api).
 *
 * The unanswered-hours steps (reminder, mediation, auto-approve) all count
 * whole days from submission, which is why every one of them says "after
 * they're submitted": that shared anchor is what makes them read as the one
 * path they are. A dispute runs on its own clock from when it is opened.
 *
 * Plain sentences, no em dashes (the Standard Terms house style, and the
 * ENG-5605 copy pass).
 */

export interface TermCopy {
  /** The row label or field name the definition belongs to. */
  label: string;
  /** One plain-English sentence: what it controls and what happens when it fires. */
  definition: string;
}

export const TERMS = {
  billingCadence: {
    label: 'Billing cadence',
    definition:
      'How often approved hours are billed: at the end of each period, the hours approved during it are charged.',
  },
  ntePacing: {
    label: 'NTE pacing',
    definition:
      "An optional weekly or monthly pace for the not-to-exceed hours; it's a guide only, so it never blocks or flags submitted hours.",
  },
  minimumHours: {
    label: 'Minimum hours',
    definition:
      "The smallest not-to-exceed ceiling an engagement on this account can be set up with; it isn't a minimum charge, because only approved hours are billed.",
  },
  governanceThreshold: {
    label: 'Governance threshold',
    definition:
      "An engagement whose not-to-exceed total is above this amount needs the team owner's approval before work starts, unless the team owner created it, and is invoiced in USD instead of drawn from credits.",
  },
  reviewReminder: {
    label: 'Review reminder',
    definition:
      "If submitted hours are still waiting for a decision this many days after they're submitted, your work approvers get a reminder.",
  },
  mediationWindow: {
    label: 'Mediation window',
    definition:
      "If submitted hours are still waiting this many days after they're submitted, your work approvers get a second notice offering OnFrontiers' help to reach a decision.",
  },
  hoursAutoApprove: {
    label: 'Hours auto-approve',
    definition:
      "Submitted hours that nobody approves or disputes within this many days after they're submitted are approved automatically and charged, with a warning to your approvers and the expert 3 days and 1 day before.",
  },
  disputeRecommendation: {
    label: 'Dispute recommendation',
    definition:
      "If a dispute over submitted hours isn't resolved within this many days, OnFrontiers sends you and the expert a summary of the record and a non-binding recommendation; OnFrontiers doesn't decide disputes.",
  },
  nteAlertThreshold: {
    label: 'NTE alert threshold',
    definition:
      "When approved hours reach this share of the not-to-exceed hours, you and the expert get an alert, so there's time to extend it with an amendment.",
  },
  invoiceTerms: {
    label: 'Invoice terms',
    definition: "USD invoices for this account are due this many days after they're issued.",
  },
} as const satisfies Record<string, TermCopy>;
